"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileOutput, FilterX, Mail, Pencil, Plus, TimerReset, Trash2 } from "lucide-react";

import { ListCount, ListSurface, ListSurfaceBody, ListSurfaceFooter, ListSurfaceHeader, ListToolbar, ListToolbarGroup, SkeletonTableRow, ToolbarIconButton } from "@/components/ds";
import { EmptyState } from "@/components/ds/empty-state";
import { EnterprisePageShell } from "@/components/layout/enterprise-page-shell";
import { FlashToast } from "@/components/ui-system/flash-toast";
import { tableHeaderSearchInputClass, ThemedSelect, themedSelectMenuClass, themedSelectTriggerClass } from "@/components/ui-system/select";
import { getDeviceById, listDevicesPage } from "@/lib/api/endpoints/devices";
import {
  createReport,
  deleteReport,
  generateReport,
  listReports,
  type ReportCreateBody,
  type ReportRecordDto,
  updateReport,
} from "@/lib/api/endpoints/reports";
import { getChannelDisplayLabel } from "@/modules/telemetry/page-utils";
import { hasModulePermission, useEffectivePermissions } from "@/modules/auth/access";
import { useAuthStore } from "@/modules/auth/store";

type FormState = {
  name: string;
  description: string;
  report_type: string;
  device_id: string;
  channel_keys: string[];
  analysis_window: string;
  frequency: string;
  export_format: string;
  recipient_emails: string;
  status: string;
};

const emptyForm: FormState = {
  name: "",
  description: "",
  report_type: "activity",
  device_id: "",
  channel_keys: [],
  analysis_window: "day",
  frequency: "never",
  export_format: "CSV",
  recipient_emails: "",
  status: "active",
};

const triggerClassName = themedSelectTriggerClass;
const menuClassName = themedSelectMenuClass;
const optionClassName = "rounded-control text-caption";
const REPORT_PAGE_SIZE_OPTIONS = [10, 25, 50];
const reportGridTemplateColumns =
  "minmax(240px,2.2fr) minmax(220px,1.8fr) minmax(140px,0.9fr) minmax(170px,1fr) minmax(220px,1.2fr) minmax(170px,auto)";

function toForm(row: ReportRecordDto): FormState {
  return {
    name: row.name,
    description: row.description ?? "",
    report_type: row.report_type,
    device_id: row.device_id ?? "",
    channel_keys: row.channel_keys ?? [],
    analysis_window: row.analysis_window,
    frequency: row.frequency,
    export_format: row.export_format,
    recipient_emails: (row.recipient_emails ?? []).join(", "),
    status: row.status,
  };
}

function toBody(form: FormState, tenantId?: string | null): ReportCreateBody {
  return {
    tenant_id: tenantId ?? null,
    name: form.name.trim(),
    description: form.description.trim() || null,
    report_type: form.report_type,
    device_id: form.device_id,
    channel_keys: form.channel_keys,
    analysis_window: form.analysis_window,
    frequency: form.frequency,
    export_format: form.export_format,
    recipient_emails: form.recipient_emails
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean),
    status: form.status,
  };
}

function formatDate(value: string | null): string {
  if (!value) return "-";
  return new Date(value).toLocaleString();
}

function titleCase(value: string): string {
  return value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatFrequency(value: string): string {
  return value === "never" ? "Manual only" : titleCase(value);
}

function formatWindow(value: string): string {
  return value === "week" ? "Last 7 Days" : "Last 24 Hours";
}

function formatChannelSummary(channels: string[]): string {
  if (!channels.length) return "No channels";
  if (channels.length <= 3) return channels.join(", ");
  return `${channels.slice(0, 3).join(", ")} +${channels.length - 3}`;
}

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function ReportModal({
  title,
  form,
  setForm,
  availableDevices,
  deviceChannels,
  busy,
  error,
  onClose,
  onSubmit,
}: {
  title: string;
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  availableDevices: { id: string; name: string }[];
  deviceChannels: { key: string; label: string; unit: string | null; dataType: "number" | "boolean" | "string" }[];
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const [fieldError, setFieldError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement | null>(null);

  function handleSubmit() {
    if (!form.name.trim()) {
      setFieldError("Report name is required.");
      nameRef.current?.focus();
      return;
    }
    if (!form.device_id) {
      setFieldError("Select a device.");
      return;
    }
    if (!form.channel_keys.length) {
      setFieldError("Select at least one channel.");
      return;
    }
    setFieldError(null);
    onSubmit();
  }

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(15, 23, 42, 0.42)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
      onClick={onClose}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 760,
          maxHeight: "calc(100vh - 32px)",
          display: "flex",
          flexDirection: "column",
          borderRadius: 24,
          border: "1px solid var(--table-border)",
          background: "var(--surface-elevated)",
          boxShadow: "var(--shadow-overlay)",
          overflow: "hidden",
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div style={{ padding: "18px 22px", borderBottom: "1px solid var(--table-border)", background: "var(--muted-bg)" }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--foreground)" }}>{title}</div>
          <div style={{ marginTop: 4, fontSize: 12, color: "var(--muted)" }}>
            Configure a real report definition with device scope, channel scope, format, and delivery.
          </div>
        </div>

        <div style={{ flex: "1 1 auto", overflowY: "auto", padding: 22, display: "grid", gap: 16 }}>
          {error || fieldError ? (
            <div style={{ border: "1px solid color-mix(in srgb, var(--error) 35%, transparent)", background: "color-mix(in srgb, var(--error) 12%, var(--card))", color: "var(--error)", borderRadius: 12, padding: "10px 12px", fontSize: 12 }}>
              {fieldError ?? error}
            </div>
          ) : null}

              <div style={{ display: "grid", gap: 12, padding: 16, border: "1px solid var(--card-border)", borderRadius: "var(--radius-card)", background: "var(--surface-secondary)" }}>
            <label style={{ display: "grid", gap: 8 }}>
              <span className="form-label form-label-required">Name</span>
              <input
                ref={nameRef}
                className="ui-control"
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              />
            </label>

            <label style={{ display: "grid", gap: 8 }}>
              <span className="form-label">Description</span>
              <textarea
                className="ui-control"
                value={form.description}
                onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                style={{ minHeight: 96 }}
              />
            </label>
          </div>

          <div style={{ display: "grid", gap: 12, padding: 16, border: "1px solid var(--table-border)", borderRadius: 16, background: "var(--muted-bg)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
              <label style={{ display: "grid", gap: 6 }}>
                <span className="form-label">Type</span>
                <ThemedSelect
                  value={form.report_type}
                  onChange={(value) => setForm((prev) => ({ ...prev, report_type: value }))}
                  options={[
                    { value: "activity", label: "Activity" },
                    { value: "threshold", label: "Threshold" },
                  ]}
                  ariaLabel="Report type"
                  triggerClassName={triggerClassName}
                  menuClassName={menuClassName}
                  optionClassName={optionClassName}
                />
              </label>

              <label style={{ display: "grid", gap: 6 }}>
                <span className="form-label">Format</span>
                <ThemedSelect
                  value={form.export_format}
                  onChange={(value) => setForm((prev) => ({ ...prev, export_format: value }))}
                  options={[
                    { value: "CSV", label: "CSV feed export" },
                    { value: "PDF", label: "PDF analysis report" },
                  ]}
                  ariaLabel="Export format"
                  triggerClassName={triggerClassName}
                  menuClassName={menuClassName}
                  optionClassName={optionClassName}
                />
              </label>

              <label style={{ display: "grid", gap: 6 }}>
                <span className="form-label">Analysis window</span>
                <ThemedSelect
                  value={form.analysis_window}
                  onChange={(value) => setForm((prev) => ({ ...prev, analysis_window: value }))}
                  options={[
                    { value: "day", label: "Last 24 Hours" },
                    { value: "week", label: "Last 7 Days" },
                  ]}
                  ariaLabel="Analysis window"
                  triggerClassName={triggerClassName}
                  menuClassName={menuClassName}
                  optionClassName={optionClassName}
                />
              </label>
            </div>
          </div>

          <div style={{ display: "grid", gap: 12, padding: 16, border: "1px solid var(--table-border)", borderRadius: 16, background: "var(--muted-bg)" }}>
            <label style={{ display: "grid", gap: 6 }}>
              <span className="form-label form-label-required">Device</span>
              <ThemedSelect
                value={form.device_id}
                onChange={(value) => setForm((prev) => ({ ...prev, device_id: value, channel_keys: [] }))}
                options={[
                  { value: "", label: "Select a device" },
                  ...availableDevices.map((device) => ({ value: device.id, label: device.name })),
                ]}
                ariaLabel="Report device"
                triggerClassName={triggerClassName}
                menuClassName={menuClassName}
                optionClassName={optionClassName}
              />
            </label>

            <div style={{ display: "grid", gap: 8 }}>
              <span className="form-label form-label-required">Channels</span>
              <div style={{ display: "grid", gap: 10, maxHeight: 196, overflowY: "auto", border: "1px solid var(--table-border)", borderRadius: 12, background: "var(--card)", padding: "12px 14px" }}>
                {deviceChannels.length ? (
                  deviceChannels.map((channel) => {
                    const checked = form.channel_keys.includes(channel.key);
                    return (
                      <label key={channel.key} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 13, color: "#374151" }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(event) =>
                            setForm((prev) => ({
                              ...prev,
                              channel_keys: event.target.checked
                                ? [...prev.channel_keys, channel.key]
                                : prev.channel_keys.filter((item) => item !== channel.key),
                            }))
                          }
                        />
                        <span style={{ fontWeight: 600 }}>{channel.label}</span>
                        <span style={{ color: "var(--muted)", fontSize: 12 }}>
                          {channel.unit || (channel.dataType === "boolean" ? "Boolean channel" : channel.dataType === "string" ? "String channel" : "Numeric channel")}
                        </span>
                      </label>
                    );
                  })
                ) : (
                  <span style={{ fontSize: 12, color: "var(--muted)" }}>
                    Select a device to load channels for reporting.
                  </span>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: "grid", gap: 12, padding: 16, border: "1px solid var(--table-border)", borderRadius: 16, background: "var(--muted-bg)" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
              <label style={{ display: "grid", gap: 6 }}>
                <span className="form-label">Email delivery</span>
                <ThemedSelect
                  value={form.frequency}
                  onChange={(value) => setForm((prev) => ({ ...prev, frequency: value }))}
                  options={[
                    { value: "never", label: "Never" },
                    { value: "daily", label: "Daily" },
                    { value: "weekly", label: "Weekly" },
                    { value: "monthly", label: "Monthly" },
                  ]}
                  ariaLabel="Email delivery frequency"
                  triggerClassName={triggerClassName}
                  menuClassName={menuClassName}
                  optionClassName={optionClassName}
                />
              </label>

              <label style={{ display: "grid", gap: 6 }}>
                <span className="form-label">Status</span>
                <ThemedSelect
                  value={form.status}
                  onChange={(value) => setForm((prev) => ({ ...prev, status: value }))}
                  options={[
                    { value: "active", label: "Active" },
                    { value: "paused", label: "Paused" },
                  ]}
                  ariaLabel="Report status"
                  triggerClassName={triggerClassName}
                  menuClassName={menuClassName}
                  optionClassName={optionClassName}
                />
              </label>
            </div>

            <label style={{ display: "grid", gap: 8 }}>
              <span className="form-label">Recipient emails</span>
              <input
                className="ui-control"
                value={form.recipient_emails}
                onChange={(event) => setForm((prev) => ({ ...prev, recipient_emails: event.target.value }))}
                placeholder="name@example.com, ops@example.com"
              />
            </label>
            <div style={{ fontSize: 12, color: "var(--muted)" }}>
              {form.frequency === "never"
                ? "Manual reports are generated in real time and download immediately without local server storage."
                : "Leave this blank to send scheduled reports to your own sign-in email by default."}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "18px 22px 22px", borderTop: "1px solid var(--table-border)", background: "var(--card)" }}>
          <button type="button" onClick={onClose} style={{ minHeight: 38, padding: "0 14px", borderRadius: 12, border: "1px solid var(--table-border)", background: "var(--card)", cursor: "pointer" }}>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={busy} style={{ minHeight: 38, padding: "0 14px", borderRadius: 12, border: "1px solid var(--btn-primary-to)", background: "var(--btn-primary-to)", color: "var(--btn-primary-fg)", cursor: "pointer" }}>
            {busy ? "Saving..." : "Save report"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ReportsPage() {
  const tenantId = useAuthStore((state) => state.tenantId);
  const user = useAuthStore((state) => state.user);
  const isSuperAdmin = user?.role === "super_admin";
  const { permissionMap } = useEffectivePermissions();
  const qc = useQueryClient();

  const canCreateReports = hasModulePermission(user, permissionMap, "reports", "create");
  const canEditReports = hasModulePermission(user, permissionMap, "reports", "edit");
  const canDeleteReports = hasModulePermission(user, permissionMap, "reports", "delete");
  const canExportReports = hasModulePermission(user, permissionMap, "reports", "export");

  const [search, setSearch] = useState("");
  const [dialog, setDialog] = useState<"create" | "edit" | null>(null);
  const [editing, setEditing] = useState<ReportRecordDto | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<number>(25);
  const effectiveTenantId = tenantId;
  const deviceWorkspaceScope: "all" | null = isSuperAdmin && !effectiveTenantId ? "all" : null;

  const reportsQuery = useQuery({
    queryKey: ["reports", effectiveTenantId ?? "all"],
    queryFn: () => listReports(effectiveTenantId),
    enabled: isSuperAdmin || Boolean(effectiveTenantId),
  });

  const devicesQuery = useQuery({
    queryKey: ["report-devices", effectiveTenantId ?? "all", deviceWorkspaceScope ?? "tenant"],
    queryFn: () =>
      listDevicesPage({
        tenantId: effectiveTenantId,
        workspaceScope: deviceWorkspaceScope,
        limit: 200,
        offset: 0,
        moduleKey: "reports",
      }),
    enabled: isSuperAdmin || Boolean(effectiveTenantId),
  });

  const selectedDeviceQuery = useQuery({
    queryKey: ["report-device-detail", form.device_id || "none"],
    queryFn: () => getDeviceById(form.device_id, null, "reports"),
    enabled: Boolean(form.device_id),
  });

  const createMutation = useMutation({
    mutationFn: () => createReport(toBody(form, effectiveTenantId)),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["reports"] });
      setDialog(null);
      setEditing(null);
      setForm(emptyForm);
      setFormError(null);
      setToast(`Report "${form.name.trim()}" saved.`);
    },
    onError: (error) => setFormError(error instanceof Error ? error.message : "Failed to save report."),
  });

  const updateMutation = useMutation({
    mutationFn: () => {
      if (!editing) throw new Error("No report selected.");
      return updateReport(editing.id, toBody(form, effectiveTenantId));
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["reports"] });
      setDialog(null);
      setEditing(null);
      setForm(emptyForm);
      setFormError(null);
      setToast(`Report "${form.name.trim()}" updated.`);
    },
    onError: (error) => setFormError(error instanceof Error ? error.message : "Failed to update report."),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteReport(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["reports"] });
      setToast("Report deleted.");
    },
  });

  const generateMutation = useMutation({
    mutationFn: async (row: ReportRecordDto) => {
      const extension = row.export_format.toLowerCase() === "pdf" ? "pdf" : "csv";
      const fallbackFileName = `${row.name.trim().replace(/\s+/g, "_").toLowerCase() || "report"}.${extension}`;
      const download = await generateReport(row.id, fallbackFileName);
      return { row, ...download };
    },
    onSuccess: async ({ row, blob, fileName }) => {
      triggerBlobDownload(blob, fileName);
      await qc.invalidateQueries({ queryKey: ["reports"] });
      setToast(`Report "${row.name}" generated.`);
    },
    onError: (error) => setToast(error instanceof Error ? error.message : "Failed to generate report."),
  });

  const rows = useMemo(() => {
    const items = reportsQuery.data?.items ?? [];
    const term = search.trim().toLowerCase();
    return items.filter((item) => {
      if (!term) return true;
      return [
        item.name,
        item.description ?? "",
        item.report_type,
        item.device_name ?? "",
        item.export_format,
      ]
        .join(" ")
        .toLowerCase()
        .includes(term);
    });
  }, [reportsQuery.data?.items, search]);
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const pageRows = useMemo(
    () => rows.slice(page * pageSize, page * pageSize + pageSize),
    [page, pageSize, rows],
  );
  const pageStart = rows.length === 0 ? 0 : page * pageSize + 1;
  const pageEnd = rows.length === 0 ? 0 : page * pageSize + pageRows.length;

  useEffect(() => {
    setPage(0);
  }, [search, pageSize, effectiveTenantId]);

  useEffect(() => {
    if (page >= pageCount) {
      setPage(Math.max(0, pageCount - 1));
    }
  }, [page, pageCount]);

  const availableDevices = useMemo(
    () =>
      (devicesQuery.data?.items ?? []).map((device) => ({
        id: device.id,
        name: device.name || device.device_uid,
      })),
    [devicesQuery.data?.items],
  );

  const availableChannels = useMemo(
    () =>
      (selectedDeviceQuery.data?.channels ?? [])
        .map((channel) => ({
          key: channel.key,
          label: getChannelDisplayLabel(channel),
          unit: channel.unit,
          dataType: channel.data_type,
        })),
    [selectedDeviceQuery.data?.channels],
  );

  function openCreate() {
    if (!canCreateReports) {
      setToast("You don't have permission to create reports.");
      return;
    }
    setEditing(null);
    setForm(emptyForm);
    setFormError(null);
    setDialog("create");
  }

  function openEdit(row: ReportRecordDto) {
    if (!canEditReports) {
      setToast("You don't have permission to edit reports.");
      return;
    }
    setEditing(row);
    setForm(toForm(row));
    setFormError(null);
    setDialog("edit");
  }

  function handleSubmit() {
    if (dialog === "create") {
      createMutation.mutate();
      return;
    }
    if (dialog === "edit") {
      updateMutation.mutate();
    }
  }

  const busy = createMutation.isPending || updateMutation.isPending;
  const hasFilters = Boolean(search.trim());

  return (
    <EnterprisePageShell
      hideHeader
      kicker="Exports"
      title="Report Delivery"
      description="Device-scoped report definitions with channel scope, on-demand generation, and optional scheduled email delivery."
    >
      <div style={{ display: "grid", gap: 20 }}>
        <ListSurface>
          {reportsQuery.isLoading ? (
            <ListSurfaceBody className="p-ds-4" aria-label="Loading report definitions">
              {Array.from({ length: 6 }, (_, index) => <SkeletonTableRow key={index} cols={6} />)}
            </ListSurfaceBody>
          ) : reportsQuery.isError ? (
            <div className="px-ds-4 py-ds-4 text-caption text-error" role="alert">
              {reportsQuery.error instanceof Error ? reportsQuery.error.message : "Failed to load reports."}
            </div>
          ) : (
            <>
            <ListSurfaceHeader
              title="Report delivery definitions"
              meta={
                <ListCount
                  total={reportsQuery.data?.items.length ?? 0}
                  filtered={rows.length}
                  noun={rows.length === 1 ? "report" : "reports"}
                />
              }
              controls={
                <ListToolbar>
                  <ListToolbarGroup grow>
                    <input
                      className={tableHeaderSearchInputClass}
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                      placeholder="Search reports"
                    />
                  </ListToolbarGroup>
                  {hasFilters ? (
                    <ToolbarIconButton icon={FilterX} label="Clear filters" onClick={() => setSearch("")} />
                  ) : null}
                  {canCreateReports ? (
                    <ToolbarIconButton icon={Plus} label="New report" variant="primary" onClick={openCreate} />
                  ) : null}
                </ListToolbar>
              }
            />
            <ListSurfaceBody className="overflow-x-auto">
            <div style={{ minWidth: 1080 }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: reportGridTemplateColumns,
                  gap: 12,
                  padding: "12px 14px",
                  borderBottom: "1px solid var(--card-border)",
                  background: "var(--bg-surface)",
                }}
              >
                {["Report", "Scope", "Type", "Delivery", "Run Timing", "Actions"].map((label) => (
                  <div key={label} style={{ fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--muted)", textAlign: label === "Actions" ? "right" : "left" }}>
                    {label}
                  </div>
                ))}
              </div>

              {pageRows.length ? (
                pageRows.map((item, index) => (
                  <div
                    key={item.id}
                    style={{
                      display: "grid",
                      gridTemplateColumns: reportGridTemplateColumns,
                      gap: 12,
                      alignItems: "center",
                      padding: "14px",
                      borderBottom: "1px solid var(--card-border)",
                  background: index % 2 === 1 ? "var(--surface-secondary)" : "var(--surface-primary)",
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                        <div style={{ fontWeight: 700, color: "var(--foreground)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.name}</div>
                        <span style={{ flexShrink: 0, borderRadius: "var(--radius-control)", background: item.status === "active" ? "var(--semantic-success-surface)" : "var(--surface-secondary)", color: item.status === "active" ? "var(--semantic-success)" : "var(--muted)", padding: "4px 8px", fontSize: 11, fontWeight: 700 }}>
                          {titleCase(item.status)}
                        </span>
                      </div>
                      <div style={{ marginTop: 4, fontSize: 12, color: "var(--muted)", lineHeight: 1.4 }}>
                        {item.description ?? "No description"}
                      </div>
                    </div>

                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#1f2937", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {item.device_name ?? "No device"}
                      </div>
                      <div style={{ marginTop: 4, fontSize: 12, color: "var(--muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {formatChannelSummary(item.channel_keys)}
                      </div>
                    </div>

                    <div style={{ display: "grid", gap: 6 }}>
                      <span style={{ display: "inline-flex", width: "fit-content", borderRadius: 999, background: "var(--muted-bg)", color: "#334155", padding: "4px 8px", fontSize: 11, fontWeight: 700 }}>
                        {titleCase(item.report_type)}
                      </span>
                      <span style={{ fontSize: 12, color: "var(--muted)" }}>
                        {item.export_format} - {formatWindow(item.analysis_window)}
                      </span>
                    </div>

                    <div style={{ display: "grid", gap: 6 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#1f2937" }}>{formatFrequency(item.frequency)}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--muted)" }}>
                        <Mail size={13} />
                        <span>{(item.recipient_emails ?? []).length ? `${item.recipient_emails.length} recipient(s)` : "No recipients"}</span>
                      </div>
                    </div>

                    <div style={{ display: "grid", gap: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--muted)" }}>
                        <TimerReset size={14} />
                        <span>Last: {formatDate(item.last_run_at)}</span>
                      </div>
                      <div style={{ fontSize: 12, color: "var(--muted)" }}>Next: {formatDate(item.next_run_at)}</div>
                    </div>

                    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 }}>
                      {canExportReports ? (
                        <button
                          type="button"
                          onClick={() => generateMutation.mutate(item)}
                          disabled={generateMutation.isPending}
                          className="inline-flex min-h-[34px] items-center gap-2 rounded-control border border-primary/15 bg-primary/10 px-3 text-micro font-semibold text-primary transition-colors hover:border-primary/25 hover:bg-primary/15"
                          title={`Generate ${item.name}`}
                        >
                          <FileOutput size={14} />
                          <span>Generate</span>
                        </button>
                      ) : null}
                      {canEditReports ? (
                        <button
                          type="button"
                          onClick={() => openEdit(item)}
                          className="inline-flex h-[34px] w-[34px] items-center justify-center rounded-control border border-primary/15 bg-primary/10 text-primary transition-colors hover:border-primary/25 hover:bg-primary/15"
                          title={`Edit ${item.name}`}
                        >
                          <Pencil size={14} />
                        </button>
                      ) : null}
                      {canDeleteReports ? (
                        <button
                          type="button"
                          onClick={() => {
                            if (!confirm(`Delete report "${item.name}"?`)) return;
                            deleteMutation.mutate(item.id);
                          }}
                          className="inline-flex h-[34px] w-[34px] items-center justify-center rounded-control border border-error/25 bg-error/10 text-error transition-colors hover:bg-error/15"
                          title={`Delete ${item.name}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ padding: "24px" }}>
                  <EmptyState
                    title="No reports match the current filters"
                    description="Create a report definition, choose a device and channels, then generate it on demand or by schedule."
                  />
                </div>
              )}
            </div>
            </ListSurfaceBody>
            <ListSurfaceFooter
              left={
                <label className="flex items-center gap-ds-2">
                  <span className="text-caption text-muted">Rows per page</span>
                  <ThemedSelect
                    value={String(pageSize)}
                    onChange={(value) => setPageSize(Number(value))}
                    ariaLabel="Rows per page"
                    className="w-[5.5rem]"
                    triggerClassName="h-10 rounded-control border-card-border bg-surface-primary hover:border-primary/20"
                    options={REPORT_PAGE_SIZE_OPTIONS.map((option) => ({ value: String(option), label: String(option) }))}
                  />
                </label>
              }
              right={
                <>
                  <span className="text-caption text-muted">
                    {rows.length === 0 ? "No pages" : `Showing ${pageStart}-${pageEnd} of ${rows.length}`}
                  </span>
                  <div className="flex items-center gap-ds-2">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      disabled={page === 0}
                      onClick={() => setPage((current) => Math.max(0, current - 1))}
                    >
                      Previous
                    </button>
                    <span className="text-caption tabular-nums text-muted">
                      Page {page + 1} / {pageCount}
                    </span>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      disabled={page + 1 >= pageCount}
                      onClick={() => setPage((current) => current + 1)}
                    >
                      Next
                    </button>
                  </div>
                </>
              }
            />
            </>
          )}
        </ListSurface>
      </div>

      {dialog ? (
        <ReportModal
          title={dialog === "create" ? "Create report" : "Edit report"}
          form={form}
          setForm={setForm}
          availableDevices={availableDevices}
          deviceChannels={availableChannels}
          busy={busy}
          error={formError}
          onClose={() => {
            setDialog(null);
            setEditing(null);
            setFormError(null);
          }}
          onSubmit={handleSubmit}
        />
      ) : null}

      {toast ? <FlashToast message={toast} onDone={() => setToast(null)} /> : null}
    </EnterprisePageShell>
  );
}
